import BenchlingConfig from 'BenchlingConfig';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import {
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
  UserFacingNodeTypes,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import type {ProcessFlowchartTaskSchemaNodeAdapter} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import doesFlowchartHaveRouterWithAndOrFunction from '../flowchart-viz/util/doesFlowchartHaveRouterWithAndOrFunction';
import {FlowchartValidator} from '../platform/FlowchartValidator';
import {PLUGIN_REGISTRY_INSTANCE} from '../platform/PluginRegistry';
import {DefaultProcessesPlugin} from '../processes-plugin/DefaultProcessesPlugin';
import ensureMergingNodesOnlyDependOnOneBranchPoint from './ensureMergingNodesOnlyDependOnOneBranchPoint';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
  ProcessFlowchartValidationResult,
  ValidationContext,
} from './ProcessFlowchartForValidationTypes';
import {filterErrorsByMode, pushErrors} from './validateProcessFlowchartHelpers';
import {
  ensureEdgeConnectsNodesInFlowchart,
  ensureControlFlowEdgeIsValid,
  ensureFlowchartDoesNotExceedMaxNumberOfNodes,
  ensureFlowchartHasIntermediateNodes,
  ensureFlowchartHasOutputNode,
  ensureFlowchartHasSingleEntryPoint,
  ensureNodeIsConnected,
  ensureNoDuplicateEdges,
  ensureNodeIsInsideDesignSpace,
  ensureNodeMappersFulfilled,
  ensureFlowchartHasTaskNode,
  ensureLoopsHaveOneEntryPoint,
  ensureLoopsContainTaskAndRouterNode,
  ensureNodeNameIsNotEmpty,
  ensureDataFlowEdgeHasAssociatedControlFlowEdge,
  ensureDataFlowEdgeFieldTypesAreValid,
  ensureDataFlowEdgeSourceIsValid,
  ensureMultipleDataFlowEdgesInTransitionDoNotTargetSameField,
  ensureRequiredFieldsHaveMappings,
} from './validateProcessFlowchartRules';
import {
  ensureNoChainedRouters,
  ensureChainedRouterLimit,
  ensureRouterNodeConnectedToNonEndNode,
  ensureRouterNodeHasTwoOutgoingEdges,
  ensureRouterNodeHasOneIncomingEdge,
  ensureRouterNodeHasRouteConfiguredForEachEdge,
  ensureRouterNodeHasOneSourceSchema,
  ensureNoMultipleRouterOnlyPathsBetweenTaskNodes,
  ensureRouterNodeDestinationNodesAreNotSamePTDV,
} from './validateProcessRouterNodes';

/**
 * Resolves which node types and execution types should be validated.
 *
 * Priority:
 * 1. Explicit params (supportedNodeTypes / supportedExecutionTypes)
 * 2. Plugin adapters (plugin.nodeAdapters)
 * 3. Defaults (all UserFacingNodeTypes / DIRECT + ENTRY + FLOWCHART)
 */
export function resolveTypesToValidate({
  supportedNodeTypes,
  supportedExecutionTypes,
  plugin,
}: {
  supportedNodeTypes?: Array<ProcessFlowchartNodeType>;
  supportedExecutionTypes?: Array<ProcessExecutionType>;
  plugin?: ProcessesPluginBase;
}): {
  nodeTypesToValidate: Array<ProcessFlowchartNodeType>;
  executionTypesToValidate: Array<ProcessExecutionType>;
} {
  const nodeTypesToValidate =
    supportedNodeTypes ??
    plugin?.nodeAdapters.map((adapter) => adapter.nodeType) ??
    Object.values(ProcessFlowchartNodeType).filter(
      (type): type is UserFacingNodeTypes => type !== ProcessFlowchartNodeType.PLACEHOLDER_NODE
    );

  const executionTypesToValidate = supportedExecutionTypes ??
    plugin?.nodeAdapters
      .filter(
        (adapter): adapter is ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> =>
          adapter.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE
      )
      .map((adapter) => adapter.executionType) ?? [
      ProcessExecutionType.DIRECT,
      ProcessExecutionType.ENTRY,
      ProcessExecutionType.FLOWCHART,
    ];

  return {nodeTypesToValidate, executionTypesToValidate};
}

interface ValidateProcessFlowchartParams {
  flowchart: ProcessFlowchartForValidation;
  validationContext: ValidationContext;
  usingFlowchartFirstConfig?: boolean;
  supportedNodeTypes?: Array<ProcessFlowchartNodeType>;
  supportedExecutionTypes?: Array<ProcessExecutionType>;
  plugin?: ProcessesPluginBase;
}

export default function validateProcessFlowchart(
  params: ValidateProcessFlowchartParams
): ProcessFlowchartValidationResult {
  const {
    flowchart,
    validationContext,
    usingFlowchartFirstConfig,
    supportedNodeTypes,
    supportedExecutionTypes,
    plugin,
  } = params;
  const {designSpace, didInitialFlowchartHaveChainedRouters} = validationContext;

  const {nodeTypesToValidate, executionTypesToValidate} = resolveTypesToValidate({
    supportedNodeTypes,
    supportedExecutionTypes,
    plugin,
  });
  const usePluginValidation =
    plugin != null ||
    (usingFlowchartFirstConfig && BenchlingConfig.WORKFLOWS_ENABLE_FLOWCHART_FIRST_CONFIG_MILESTONE_1);
  let validator: FlowchartValidator | undefined;
  if (usePluginValidation) {
    validator =
      plugin?.flowchartValidator ??
      PLUGIN_REGISTRY_INSTANCE.getPlugin(DefaultProcessesPlugin.pluginName).flowchartValidator;
  }
  let errors: Array<ProcessFlowchartValidationError> = [];
  const disconnectedNodeIds: Array<string> = [];
  const {nodes, edges} = flowchart;

  // Base flowchart validation rules
  errors = pushErrors(errors, ensureFlowchartDoesNotExceedMaxNumberOfNodes(flowchart), validator);

  for (const node of nodes) {
    errors = pushErrors(errors, ensureNodeIsInsideDesignSpace(node, designSpace), validator);
    const error = ensureNodeIsConnected({
      node,
      nodes,
      edges,
      usingFlowchartFirstConfig: usingFlowchartFirstConfig ?? false,
    });
    if (error != null) {
      disconnectedNodeIds.push(node.id);
      errors = pushErrors(errors, error, validator);
    }
    errors = pushErrors(
      errors,
      ensureNodeMappersFulfilled(node, flowchart, designSpace, usingFlowchartFirstConfig),
      validator
    );
    errors = pushErrors(errors, ensureRequiredFieldsHaveMappings(node, flowchart), validator);
    errors = pushErrors(
      errors,
      ensureNodeNameIsNotEmpty({
        node,
        usingFlowchartFirstConfig: usingFlowchartFirstConfig ?? false,
      }),
      validator
    );
  }

  if (nodeTypesToValidate.includes(ProcessFlowchartNodeType.TASK_SCHEMA_NODE)) {
    errors = pushErrors(errors, ensureFlowchartHasTaskNode(flowchart.nodes), validator);
  }

  errors = pushErrors(errors, ensureFlowchartHasIntermediateNodes(nodes), validator);

  // If a flowchart has disconnected nodes, no need to also add this message
  if (disconnectedNodeIds.length === 0) {
    errors = pushErrors(
      errors,
      ensureFlowchartHasSingleEntryPoint({
        nodes,
        edges,
        usingFlowchartFirstConfig: usingFlowchartFirstConfig ?? false,
      }),
      validator
    );
  }

  for (const edge of edges) {
    errors = pushErrors(
      errors,
      ensureControlFlowEdgeIsValid({
        edge,
        flowchart,
        designSpace,
        usingFlowchartFirstConfig,
      }),
      validator
    );
    errors = pushErrors(errors, ensureEdgeConnectsNodesInFlowchart(edge, flowchart), validator);
    errors = pushErrors(errors, ensureNoDuplicateEdges(edge, flowchart), validator);
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      errors = pushErrors(errors, ensureDataFlowEdgeHasAssociatedControlFlowEdge(edge, flowchart), validator);
      errors = pushErrors(
        errors,
        ensureDataFlowEdgeSourceIsValid({
          edge,
          flowchart,
          usingFlowchartFirstConfig: usingFlowchartFirstConfig ?? false,
        }),
        validator
      );
      errors = pushErrors(errors, ensureDataFlowEdgeFieldTypesAreValid(edge, flowchart), validator);
    }
  }

  errors = pushErrors(
    errors,
    ensureMultipleDataFlowEdgesInTransitionDoNotTargetSameField(flowchart, validationContext),
    validator
  );
  errors = pushErrors(
    errors,
    ensureMergingNodesOnlyDependOnOneBranchPoint(flowchart, designSpace, {
      validationContext,
      usingFlowchartFirstConfig,
    }),
    validator
  );

  if (usePluginValidation) {
    // Update validationContext with computed disconnectedNodeIds
    const contextWithDisconnectedNodes: ValidationContext = {
      ...validationContext,
      disconnectedNodeIds,
    };
    for (const nodeType of nodeTypesToValidate) {
      if (nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        for (const executionType of executionTypesToValidate) {
          const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
          const nodeTypeErrors = adapter.validateFlowchartForNodeType(
            flowchart,
            contextWithDisconnectedNodes
          );
          errors = pushErrors(errors, nodeTypeErrors, validator);
        }
      } else {
        const adapter = PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(nodeType);
        const nodeTypeErrors = adapter.validateFlowchartForNodeType(flowchart, contextWithDisconnectedNodes);
        errors = pushErrors(errors, nodeTypeErrors, validator);
      }
    }
    errors = pushErrors(errors, validator!.validateFlowchartForType(flowchart, contextWithDisconnectedNodes));
  } else {
    // TODO: Remove this once we have migrated to the new flowchart config editor
    errors = pushErrors(
      errors,
      ensureFlowchartHasOutputNode({
        nodes,
        designSpace,
      }).error
    );
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        if (disconnectedNodeIds == null || !disconnectedNodeIds.includes(node.id)) {
          errors = pushErrors(errors, ensureRouterNodeHasOneIncomingEdge(node, flowchart.edges));
          errors = pushErrors(errors, ensureRouterNodeHasTwoOutgoingEdges(node, flowchart.edges));
          errors = pushErrors(errors, ensureRouterNodeConnectedToNonEndNode(node, flowchart));
        }
        if (
          !didInitialFlowchartHaveChainedRouters ||
          (didInitialFlowchartHaveChainedRouters && doesFlowchartHaveRouterWithAndOrFunction(flowchart))
        ) {
          errors = pushErrors(errors, ensureNoChainedRouters(node, flowchart));
        }
        errors = pushErrors(
          errors,
          ensureRouterNodeDestinationNodesAreNotSamePTDV({
            node,
            flowchart,
            usingFlowchartFirstConfig: usingFlowchartFirstConfig ?? false,
          })
        );
        errors = pushErrors(errors, ensureRouterNodeHasRouteConfiguredForEachEdge(node, flowchart));
        errors = pushErrors(
          errors,
          ensureRouterNodeHasOneSourceSchema({
            node,
            flowchart,
            designSpace,
            usingFlowchartFirstConfig: false,
          })
        );
        errors = pushErrors(errors, ensureChainedRouterLimit(node, flowchart));
      } else if (node.nodeType !== ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
        errors = pushErrors(
          errors,
          ensureNoMultipleRouterOnlyPathsBetweenTaskNodes({
            destinationNode: node,
            flowchart,
            usingFlowchartFirstConfig: usingFlowchartFirstConfig ?? false,
          })
        );
      }
      errors = pushErrors(errors, ensureLoopsContainTaskAndRouterNode(node, flowchart));
      errors = pushErrors(errors, ensureLoopsHaveOneEntryPoint(flowchart));
    }
  }

  // Filter errors based on the current editor context
  const filteredErrors = filterErrorsByMode(errors, validationContext.editorMode);

  return {errors: filteredErrors};
}
